import React, { FC, useCallback, useMemo } from "react";
import { FormRenderProps, useForm, useFormState } from "react-final-form";
import { Field } from "react-final-form";
import { makeStyles } from "@material-ui/core";
import { Theme } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import Hidden from "@material-ui/core/Hidden";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Loading from "../../../../components/Loading";
import BootstrapInput from "../../../../components/BootstrapInput";
import PasswordInput from "../../../../components/PasswordInput";
import Button from "../../../../components/Button";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import TermsApproval from "../TermsApproval";

const useStyles = makeStyles((theme: Theme) => ({
  fieldsContainer: {
    marginBottom: -4,
    "& .MuiGrid-item": {
      padding: 8,
    },
  },
  divider: {
    marginTop: 12,
    marginBottom: 24,
  },
  root: {
    width: "100%",
    marginTop: 28,
  },
  input: {
    "& label": {
      position: "absolute",
      display: "block",
      color: "#a1a4ac",
      fontSize: 14,
      fontWeight: 400,
      left: 0,
      top: -19,
      transform: "scale(1)",
    },
    "& label + div": {
      marginTop: 0,
      "&::before, &::after": {
        content: "none",
      },
      "& .MuiPhoneNumber-flagButton": {
        padding: "0 !important",
      },
    },
    lineHeight: "1 !important",
    fontSize: "16px !important",
    borderRadius: "15px !important",
    opacity: 1,
    width: "100% !important",
    height: "auto !important",
    position: "relative",
    border: "1px solid #ced4da !important",
    padding: "5px 16px 5px 16px",
    boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.04)",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    marginTop: 18,
    boxSizing: "border-box",
    "&:focus": {
      borderColor: "#7d797a",
    },
    "&::placeholder": {
      color: "#9195a4",
      opacity: 1,
    },
    "&:-ms-input-placeholder": {
      color: "#9195a4",
    },
    "&::-ms-input-placeholder": {
      color: "#9195a4",
    },
  },
  recaptchaText: {
    marginTop: 8,
  },
}));

const RegisterParentForm: FC<FormRenderProps> = ({
  initialValues,
  handleSubmit,
  submitError,
  submitErrors,
  submitting,
  ...props
}) => {
  const classes = useStyles();
  const { values } = useFormState();
  const { change } = useForm();

  const files = useMemo(
    () => (values.image ? [values.image] : []),
    [values.image],
  );

  const addNewFiles = useCallback(
    async (files) => {
      const file = files[0];

      change("image", {
        id: 1,
        name: file.name,
        size: file.size,
        file,
      });
    },
    [change],
  );

  const removeFileHandler = useCallback(() => {
    change("image", null);
  }, [change]);

  return (
    <form noValidate onSubmit={handleSubmit}>
      {submitError && <Typography color="error">{submitError}</Typography>}
      {submitting && <Loading />}

      <Grid container spacing={3} className={classes.fieldsContainer}>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="phone"
            label="Phone Number"
            placeholder="+19159969739"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="name"
            label="Full Name"
            placeholder="John Smith"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="email"
            label="Email"
            placeholder="example@test.com"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="password"
            label="Password"
            placeholder="Must have at least 8 characters"
            autoComplete="new-password"
            component={PasswordInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            label="Repeat Password"
            placeholder="Must have at least 8 characters"
            name="password_confirmation"
            component={PasswordInput}
          />
        </Grid>
      </Grid>

      <Hidden smDown>
        <Divider className={classes.divider} />
      </Hidden>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <UploadFile
            uploadedFiles={files}
            addNewFiles={addNewFiles}
            removeFile={removeFileHandler}
            titleText={"Photo"}
            subText={"Drag & Drop Your image"}
            allowedFileTypes={["jpg", "jpeg", "png"]}
            maxSize={5}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <TermsApproval termsText={""} />
          <Button
            fullWidth
            type="submit"
            color="secondary"
            variant="contained"
            disabled={submitting}
          >
            Sign Up
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <Typography
            component="p"
            align="center"
            className={classes.recaptchaText}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
            <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
            apply.
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default RegisterParentForm;
